/**
 * API Service Layer
 * Handles all backend API calls with secure cookie-based authentication
 * 
 * SECURITY: Uses HTTP-only cookies instead of localStorage
 * - Tokens are sent automatically via cookies
 * - No manual token handling in JavaScript
 * - Protected from XSS attacks
 */

import { API_BASE_URL } from '../config/environment';
import s3Service from './s3Service';
import cacheService from './cacheService';
import secureAuthService from './secureAuthService';

class ApiService {
  constructor() {
    this.isRefreshing = false;
    this.refreshPromise = null;
    // PERFORMANCE OPTIMIZATION: Enhanced request deduplication
    this.pendingRequests = new Map();
    // Request coalescing - group similar requests
    this.requestQueue = new Map();
    this.queueTimer = null;
  }

  /**
   * Get authentication headers
   * 
   * SECURITY: No longer fetches tokens from Amplify/localStorage
   * Cookies are sent automatically by the browser
   */
  async getAuthHeaders() {
    // Return basic headers - cookies are sent automatically
    return {
      'Accept': 'application/json',
    };
  }
  
    /**
     * Generic fetch wrapper with error handling and cookie support
     * PERFORMANCE OPTIMIZATION: Enhanced request deduplication and coalescing
     */
    async fetchData(endpoint) {
      // Check cache first (if integrated with cacheService)
      const cacheKey = `api:${endpoint}`;
      const cached = cacheService.get(cacheKey);
      if (cached !== null) {
        return cached;
      }

      // Check if there's already a pending request for this endpoint
      if (this.pendingRequests.has(endpoint)) {
        // Return the existing promise to avoid duplicate requests
        return this.pendingRequests.get(endpoint);
      }

      // Create the request promise
      const requestPromise = this._executeRequest(endpoint);
    
      // Store it in pending requests
      this.pendingRequests.set(endpoint, requestPromise);
    
      // Clean up after request completes (success or failure)
      requestPromise
        .then(data => {
          if (endpoint.includes('/residents/') || endpoint.includes('/caregivers/')) {
            cacheService.set(cacheKey, data, 30000);
          }
          return data;
        })
        .catch(() => {})
        .finally(() => {
          this.pendingRequests.delete(endpoint);
        });
    
      return requestPromise;
    }

  /**
   * Build and validate a full URL from a path segment.
   * Ensures every fetch() in this class targets the same validated origin.
   * Supports both absolute URLs (local dev) and relative paths (deployed via Nginx).
   */
  _buildUrl(path) {
    const fullUrl = `${API_BASE_URL}${path}`;

    // If API_BASE_URL is relative (e.g. "/api/v1"), resolve against current origin
    const base = window.location.origin;
    const resolvedApi = new URL(API_BASE_URL, base);
    const resolvedFull = new URL(fullUrl, base);

    if (resolvedFull.origin !== resolvedApi.origin) {
      throw new Error(`Blocked request to disallowed origin: ${resolvedFull.origin}`);
    }
    return fullUrl;
  }

  /**
   * Internal method to execute the actual request
   */
  async _executeRequest(endpoint) {
    try {
      const fullUrl = this._buildUrl(endpoint);
    
      // Add timeout to detect hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
      // Get auth headers (no tokens - cookies sent automatically)
      const headers = await this.getAuthHeaders();
    
      try {
        const response = await fetch(fullUrl, {
          signal: controller.signal,
          mode: 'cors',
          credentials: 'include', // CRITICAL: Send cookies with request
          headers: headers
        });
        clearTimeout(timeoutId);
      
                if (!response.ok) {
                            // Handle 401 Unauthorized - try to refresh tokens first
                  if (response.status === 401) {
                    // Unauthorized - attempting token refresh (no logging)
            
                    // Try to refresh tokens
                    try {
                      // Prevent multiple simultaneous refresh attempts
                      if (!this.isRefreshing) {
                        this.isRefreshing = true;
                        this.refreshPromise = secureAuthService.refreshTokens();
                      }
              
                      const refreshResult = await this.refreshPromise;
                      this.isRefreshing = false;
                      this.refreshPromise = null;
              
                      if (refreshResult.success) {
                        // Token refresh successful - retrying request
                        // Retry the original request with new tokens
                        const retryResponse = await fetch(this._buildUrl(endpoint), {
                          signal: controller.signal,
                          mode: 'cors',
                          credentials: 'include',
                          headers: headers
                        });
                
                        if (retryResponse.ok) {
                          return await retryResponse.json();
                        }
                      }
                    } catch (refreshError) {
                      // Token refresh failed (no logging to avoid session exposure)
                    }
            
                                // If refresh failed or retry failed, redirect to login
                    window.location.href = '/login';
                    throw new Error('Session expired - please log in again');
                  }
          
                  // Handle 404 for health score endpoint - don't throw error
                  if (response.status === 404 && endpoint.includes('/health-score/')) {
                    // Health score not found - return null instead of throwing
                    return null;
                  }
          
                  const errorText = await response.text();
                  throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
                }
      
        const data = await response.json();
        return data;
      } catch (fetchError) {
        clearTimeout(timeoutId);
      
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out - backend server may not be responding');
        }
        throw fetchError;
      }
    } catch (error) {
    
      // Provide more helpful error messages
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Network error - please check your internet connection and try again.');
      }
    
      throw error;
    }
  }

    /**
   * DASHBOARD API
   */
  async getDashboardMetrics() {
    const cacheKey = cacheService.generateKey('dashboard', 'metrics');
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData('/dashboard/metrics/'),
      cacheService.defaultTTL.analytics // 5 minutes cache
    );
  }

  /**
   * SLEEP DIARY API
   */
    async getSleepData(residentId) {
    return this.fetchData(`/sleep/${encodeURIComponent(residentId)}/`);
  }

    async getSleepSummary(residentId, days = 7) {
    return this.fetchData(`/sleep/${encodeURIComponent(residentId)}/summary/?days=${days}`);
  }

    /**
   * GAIT ANALYSIS API
   */
    async getGaitData(residentId, periodOptions = {}) {
    // Build query params from period options
    const params = new URLSearchParams();
    if (periodOptions.stepFreqPeriod) params.append('step_freq_period', periodOptions.stepFreqPeriod);
    if (periodOptions.stridePeriod) params.append('stride_period', periodOptions.stridePeriod);
    if (periodOptions.armSwingPeriod) params.append('arm_swing_period', periodOptions.armSwingPeriod);
    const query = params.toString();
    return this.fetchData(`/gait/${encodeURIComponent(residentId)}/${query ? `?${query}` : ''}`);
  }

    async getGaitMetrics(residentId) {
    return this.fetchData(`/gait/${encodeURIComponent(residentId)}/metrics/`);
  }

  /**
   * FACILITY GAIT OVERVIEW API
   */
  async getFacilityGaitOverview() {
    return this.fetchData('/gait/facility/overview');
  }

  /**
   * FACILITY SLEEP OVERVIEW API
   */
  async getFacilitySleepOverview() {
    return this.fetchData('/sleep/facility/overview');
  }

  /**
   * ADLs (Activities of Daily Living) API
   */
    async getADLSData(residentId) {
    return this.fetchData(`/adls/${encodeURIComponent(residentId)}/`);
  }

    /**
   * RESIDENTS API
   */
                async getAllResidents() {
    const cacheKey = cacheService.generateKey('residents', 'all');
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData('/residents/'),
      cacheService.defaultTTL.residents
    );
  }

    async getResident(residentId) {
    const cacheKey = cacheService.generateKey('resident', residentId);
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData(`/residents/${encodeURIComponent(residentId)}/`),
      cacheService.defaultTTL.residents
    );
  }

    async getResidentAlerts(residentId, limit = 10) {
    return this.fetchData(`/residents/${encodeURIComponent(residentId)}/alerts/?limit=${limit}`);
  }

    async getResidentSuggestions(residentId, limit = 10) {
    return this.fetchData(`/residents/${encodeURIComponent(residentId)}/suggestions/?limit=${limit}`);
  }

    async getHealthScore(residentId) {
      const cacheKey = cacheService.generateKey('health-score', residentId);
      return cacheService.getOrFetch(
        cacheKey,
        () => this.fetchData(`/residents/${encodeURIComponent(residentId)}/health-score/`),
        cacheService.defaultTTL.residents
      );
    }

    /**
     * DELETE RESIDENT (also deletes photo from S3 on backend)
     */
    async deleteResident(residentId) {
      try {
        const headers = await this.getAuthHeaders();
        const response = await fetch(
          this._buildUrl(`/residents/${encodeURIComponent(residentId)}/`),
          { 
            method: 'DELETE',
            credentials: 'include',
            headers: headers
          }
        );
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }

        // Invalidate residents and dashboard cache after deletion
        cacheService.clearPattern('residents');
        cacheService.clearPattern('dashboard');

        return await response.json();
      } catch (error) {
        throw error;
      }
    }

    /**
     * CREATE RESIDENT (multipart form with optional photo)
     */
    async createResident(residentData, photoFile = null) {
      try {
        const formData = new FormData();
        formData.append('resident_data', JSON.stringify(residentData));
      
        if (photoFile) {
          formData.append('photo', photoFile);
        }
      
        const response = await fetch(this._buildUrl('/residents/'), {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }
      
        // Invalidate residents and dashboard cache after creation
        cacheService.clearPattern('residents');
        cacheService.clearPattern('dashboard');

        return await response.json();
      } catch (error) {
        throw error;
      }
    }

          /**
   * UPDATE RESIDENT
   */
  async updateResident(residentId, residentData) {
    try {
        const authHeaders = await this.getAuthHeaders();
        const response = await fetch(
          this._buildUrl(`/residents/${encodeURIComponent(residentId)}/`),
          {
            method: 'PUT',
            credentials: 'include',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(residentData),
          }
        );
    
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }

        // Invalidate residents and dashboard cache after update
        cacheService.clearPattern('residents');
        cacheService.clearPattern('dashboard');
    
        return await response.json();
    } catch (error) {
        throw error;
    }
  }

    /**
   * RESIDENT DETAILS API
   */
                        async getResidentDetails(residentId) {
    const data = await this.fetchData(`/residents/${encodeURIComponent(residentId)}/`);
    
    // Extract name from status_name_sort field: "ACTIVE--LastName--FirstName--RES--..."
    let displayName = 'Unknown Resident';
    if (data.status_name_sort) {
      const parts = data.status_name_sort.split('--');
      if (parts.length >= 3) {
        const lastName = parts[1] || '';
        const firstName = parts[2] || '';
        displayName = `${firstName} ${lastName}`.trim();
      }
    }
    
    // Fetch photo URL directly from S3 using Amplify Storage (Cognito Identity Pool)
    let profilePhoto = null;
    if (data.photo_s3_key && data.photo_s3_key.length > 0) {
      profilePhoto = await s3Service.getPhotoUrl(data.photo_s3_key);
    }
    
                // Clean room_id - remove ROOM- prefix
    const cleanRoomId = (data.room_id || '')
      .replace(/^ROOM-/, '') || '';
    
            // Keep the full bed_id as-is (e.g., BED-0201-A)
    const cleanBedId = data.bed_id || '';
    
    // Clean unit_id if needed
    const cleanUnitId = data.unit_id || '';
    
    // Add the extracted name and photo URL to the data
    return {
      ...data,
      name: displayName,
            profile_photo: profilePhoto,
      room_id: cleanRoomId,
      bed_id: cleanBedId, // Full bed_id format: BED-0201-A
      unit_id: cleanUnitId,
    };
  }

        async getEmergencyContacts(residentId) {
    try {
      const data = await this.fetchData(`/residents/${encodeURIComponent(residentId)}/emergency-contacts/`);
      return data.contacts || [];
    } catch (error) {
      // Error fetching emergency contacts (no logging to avoid PHI exposure)
      return [];
    }
  }

      /**
   * UPDATE EMERGENCY CONTACTS
   */
  async updateEmergencyContacts(residentId, contacts) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/residents/${encodeURIComponent(residentId)}/emergency-contacts/`),
        {
          method: 'PUT',
          credentials: 'include', // Send cookies
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ contacts }),
        }
      );
    
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
    
      return await response.json();
    } catch (error) {
      // API Error [PUT /residents/:id/emergency-contacts] (no logging to avoid PHI exposure)
      throw error;
    }
  }

    async getResidentCaregivers(residentId, days = 7) {
    return this.fetchData(`/residents/${encodeURIComponent(residentId)}/caregivers/?days=${days}`);
  }

        async getResidentHighlights(residentId) {
    // Pull real data from health-score, sleep summary, gait snapshot, and falls
    const highlights = {
        last_sleep_hours: null,
        last_sleep_date: null,
        recent_falls_count: null,
        gait_score: null,
        adl_completion: null
    };

    try {
        const healthScore = await this.getHealthScore(residentId);
        if (healthScore) {
          highlights.gait_score = healthScore.gait_stability_score || null;
          highlights.adl_completion = healthScore.activity_level_score || null;
        
          if (healthScore.sleep_card?.line1) {
            const sleepHrs = parseFloat(healthScore.sleep_card.line1);
            if (!isNaN(sleepHrs)) highlights.last_sleep_hours = sleepHrs;
          }
        }
    } catch (e) {
        // Silently handle 404 or any other health score errors
        // Health score is optional data
    }

    try {
      const sleepData = await this.fetchData(`/sleep/${encodeURIComponent(residentId)}/summary/?days=1`);
      if (sleepData && sleepData.length > 0) {
        const latest = sleepData[0];
        const tst = latest.total_sleep_time_min;
        if (tst) highlights.last_sleep_hours = Math.round((tst / 60) * 10) / 10;
        if (latest.sleep_date) highlights.last_sleep_date = latest.sleep_date;
      }
    } catch (e) {
      // Silent fail
    }

    // Get actual recent falls count from falls API (last 30 days)
    try {
      const fallsData = await this.getAllFalls(30);
      if (fallsData && fallsData.falls) {
        // Filter falls for this specific resident
        const residentFalls = fallsData.falls.filter(fall => fall.resident_id === residentId);
        highlights.recent_falls_count = residentFalls.length;
      }
    } catch (e) {
      // Silent fail
    }

    return highlights;
  }

            /**
       * DEVICES API
       */
                        async getDevices() {
        try {
          return await this.fetchData('/devices/');
          } catch (error) {
            // Error fetching devices (no logging)
          // Return empty array if devices endpoint fails
          return { devices: [] };
        }
      }

            /**
             * FALLS API - WITH PAGINATION SUPPORT
             */
                  async getAllFalls(days = 30, page = 1, limit = 3) {
              const cacheKey = cacheService.generateKey('falls', 'all', days, page, limit);
              return cacheService.getOrFetch(
                cacheKey,
                () => this.fetchData(`/falls/?days=${days}&page=${page}&limit=${limit}`),
                cacheService.defaultTTL.falls
              );
            }

    async getFallsByPriority(priorityLevel, days = 30) {
    return this.fetchData(`/falls/?priority=${priorityLevel}&days=${days}`);
  }

                async getFallAnalytics(period = 'hourly', residentId = null) {
    const cacheKey = residentId
      ? cacheService.generateKey('analytics', period, residentId)
      : cacheService.generateKey('analytics', period, 'all');
    
    const endpoint = residentId 
      ? `/falls/analytics/?period=${period}&resident_id=${encodeURIComponent(residentId)}`
      : `/falls/analytics/?period=${period}`;
    
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData(endpoint),
      cacheService.defaultTTL.analytics
    );
  }

    async getFallVideo(fallId) {
      return this.fetchData(`/falls/video/${encodeURIComponent(fallId)}/`);
    }

    /**
     * MARK FALL AS FALSE POSITIVE
     * Marks a fall as false positive and moves video to training data bucket
     */
    async markFallAsFalsePositive(fallId, reason = null) {
      try {
        const authHeaders = await this.getAuthHeaders();
        const response = await fetch(
          this._buildUrl('/falls/false-positive'),
          {
            method: 'POST',
            credentials: 'include', // Send cookies
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fall_id: fallId,
              reason: reason
            }),
          }
        );

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }

                // Invalidate falls cache after marking false positive
        cacheService.clearPattern('falls');
        cacheService.clearPattern('analytics');

        return await response.json();
      } catch (error) {
        // API Error [POST /falls/false-positive] (no logging to avoid PHI exposure)
        throw error;
      }
    }

  /**
   * CAREGIVERS API
   */
    async getAllCaregivers() {
    const cacheKey = cacheService.generateKey('caregivers', 'all');
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData('/caregivers/'),
      cacheService.defaultTTL.residents // 5 minutes
    );
  }

    async getCaregiver(caregiverId) {
    const cacheKey = cacheService.generateKey('caregiver', caregiverId);
    return cacheService.getOrFetch(
      cacheKey,
      () => this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/`),
      cacheService.defaultTTL.residents
    );
  }

        async getCaregiverCertifications(caregiverId) {
    try {
      const data = await this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/certifications/`);
      
      // If we got certifications, calculate days_until_expiry
      if (data && data.certifications && data.certifications.length > 0) {
        const processedCerts = data.certifications.map(cert => {
          let daysUntilExpiry = null;
          if (cert.expiry_date) {
            const expiryDate = new Date(cert.expiry_date);
            const today = new Date();
            daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          }
          
          return {
            ...cert,
            days_until_expiry: daysUntilExpiry
          };
        });
        
        return {
          certifications: processedCerts
        };
      }
      
      // No certifications found - return empty
      return {
        certifications: []
      };
    } catch (error) {
      // Error fetching certifications (no logging to avoid PHI exposure)
      throw error;
    }
  }

    /**
   * ADD NEW CERTIFICATIONS
   * Adds new certifications to a caregiver
   */
  async addCaregiverCertifications(caregiverId, certifications) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/caregivers/${encodeURIComponent(caregiverId)}/certifications/`),
        {
          method: 'POST',
          credentials: 'include', // Send cookies
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ certifications }),
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [POST /caregivers/:id/certifications] (no logging to avoid PHI exposure)
      throw error;
    }
  }

  /**
   * UPDATE SINGLE CERTIFICATION
   * Updates a specific certification for a caregiver
   */
  async updateCaregiverCertification(caregiverId, certType, certificationData) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/caregivers/${encodeURIComponent(caregiverId)}/certifications/${encodeURIComponent(certType)}/`),
        {
          method: 'PUT',
          credentials: 'include', // Send cookies
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(certificationData),
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [PUT /caregivers/:id/certifications/:certType] (no logging to avoid PHI exposure)
      throw error;
    }
  }

    /**
   * DELETE SINGLE CERTIFICATION
   * Deletes a specific certification for a caregiver
   */
  async deleteCaregiverCertification(caregiverId, certType) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/caregivers/${encodeURIComponent(caregiverId)}/certifications/${encodeURIComponent(certType)}/`),
        {
          method: 'DELETE',
          credentials: 'include', // Send cookies
          headers: authHeaders,
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [DELETE /caregivers/:id/certifications/:certType] (no logging to avoid PHI exposure)
      throw error;
    }
  }

  async getCaregiverAssignments(caregiverId, days = 7) {
    return this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/assignments/?days=${days}`);
  }

    async getCaregiverSchedule(caregiverId, days = 7) {
    return this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/schedule/?days=${days}`);
  }

    async getCaregiverPerformance(caregiverId) {
    try {
      const data = await this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/performance/`);
      
      // Return data as is from backend
      return data;
    } catch (error) {
      // Error fetching performance (no logging to avoid PHI exposure)
      throw error;
    }
  }

      /**
   * CREATE CAREGIVER (multipart form with optional photo)
   */
  async createCaregiver(caregiverData, photoFile = null) {
    try {
      const formData = new FormData();
      formData.append('caregiver_data', JSON.stringify(caregiverData));
      
      if (photoFile) {
        formData.append('photo', photoFile);
      }
      
      const response = await fetch(this._buildUrl('/caregivers/'), {
        method: 'POST',
        credentials: 'include', // Send cookies
        body: formData
        // Don't set Content-Type header - browser will set it with boundary for multipart
      });
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [POST /caregivers] (no logging to avoid PHI exposure)
      throw error;
    }
  }

  /**
   * UPDATE CAREGIVER
   */
  async updateCaregiver(caregiverId, caregiverData) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/caregivers/${encodeURIComponent(caregiverId)}/`),
        {
          method: 'PUT',
          credentials: 'include', // Send cookies
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(caregiverData),
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [PUT /caregivers] (no logging to avoid PHI exposure)
      throw error;
    }
  }

  /**
   * DELETE CAREGIVER (also deletes photo from S3 on backend)
   */
  async deleteCaregiver(caregiverId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/caregivers/${encodeURIComponent(caregiverId)}/`),
        { 
          method: 'DELETE',
          credentials: 'include', // Send cookies
          headers: headers
        }
      );
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      return await response.json();
    } catch (error) {
      // API Error [DELETE /caregivers] (no logging to avoid PHI exposure)
      throw error;
    }
  }

    /**
   * CAREGIVER DETAILS (processed)
   */
            async getCaregiverDetails(caregiverId) {
    const data = await this.fetchData(`/caregivers/${encodeURIComponent(caregiverId)}/`);
    
    // Extract display name
    let displayName = data.display_name || 'Unknown Caregiver';
    
    // Fetch photo URL directly from S3 using Amplify Storage (Cognito Identity Pool)
    let profilePhoto = null;
    if (data.photo_s3_key) {
      profilePhoto = await s3Service.getPhotoUrl(data.photo_s3_key);
    }
    
    return {
      ...data,
      name: displayName,
      profile_photo: profilePhoto
    };
  }

  /**
   * CAREGIVER-RESIDENT ASSIGNMENT API
   */
  
  // Get available caregivers for assignment
    async getAvailableCaregivers() {
    return this.fetchData('/residents/caregivers/available/');
  }

      // Assign caregiver to resident
  async assignCaregiverToResident(residentId, assignmentData) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/residents/${encodeURIComponent(residentId)}/caregivers/`),
        {
          method: 'POST',
          credentials: 'include', // Send cookies
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(assignmentData),
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [POST /residents/:id/caregivers] (no logging to avoid PHI exposure)
      throw error;
    }
  }

  // Remove caregiver assignment
  async removeCaregiverFromResident(residentId, caregiverId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        this._buildUrl(`/residents/${encodeURIComponent(residentId)}/caregivers/${encodeURIComponent(caregiverId)}/`),
        { 
          method: 'DELETE',
          credentials: 'include', // Send cookies
          headers: headers
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorBody}`);
      }
      
      return await response.json();
    } catch (error) {
      // API Error [DELETE /residents/:id/caregivers/:cgId] (no logging to avoid PHI exposure)
      throw error;
    }
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

